import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { resolveEffectiveThreshold } from "@capture/shared";
import { usePaneStore } from "../state/store-context";
import { selectBatchLabel } from "../state/store";
import { useShallow } from "zustand/react/shallow";
import type { FieldState, SessionStats } from "../state/types";
import { formatDuration } from "../lib/format-duration";
import { useFieldFocusAndSelect } from "../hooks/useFieldFocusAndSelect";

function getStatusIndicator(field: FieldState, isActive: boolean, jobTypeThreshold: number | null): string {
  if (isActive) return "\u2190"; // ←
  if (field.validationStatus === "fail") return "\u2717"; // ✗
  if (field.validationStatus === "overridden") return "~";
  if (field.validationStatus === "pass") return "\u2713"; // ✓

  // At this point, validationStatus is "pending" (all other cases returned early).
  // Check if high-confidence OCR makes this effectively auto-verified.
  const isAutoVerified =
    field.ocrConfidence !== null &&
    // F-CAPTURE-044 — the station's own threshold (field -> job type -> default).
    field.ocrConfidence >= resolveEffectiveThreshold(field.verifyThreshold, jobTypeThreshold) &&
    !field.isDirty &&
    !(field.required && field.value === "");
  if (isAutoVerified) return "\u2713"; // ✓

  return " ";
}

function formatDocsPerHour(docs: number, elapsedMs: number): string {
  if (elapsedMs <= 0 || docs === 0) return "0.0";
  const hours = elapsedMs / 3_600_000;
  return (docs / hours).toFixed(1);
}

function formatAvgTimePerDoc(docs: number, elapsedMs: number): string {
  if (docs === 0) return "--";
  const avgMs = elapsedMs / docs;
  const avgSeconds = Math.floor(avgMs / 1000);
  const minutes = Math.floor(avgSeconds / 60);
  const seconds = avgSeconds % 60;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

interface TerminalFieldProps {
  fieldIndex: number;
  maxLabelLength: number;
}

function TerminalField({ fieldIndex, maxLabelLength }: TerminalFieldProps) {
  const field = usePaneStore(useShallow((s) => s.fields[fieldIndex]));
  const isActive = usePaneStore((s) => s.activeFieldIndex === fieldIndex);
  const setFieldValue = usePaneStore((s) => s.setFieldValue);
  const setActiveField = usePaneStore((s) => s.setActiveField);
  const verifyThreshold = usePaneStore((s) => s.verifyThreshold);
  const incrementSessionStat = usePaneStore((s) => s.incrementSessionStat);

  const inputRef = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(field?.value ?? "");

  useEffect(() => {
    if (field) setLocalValue(field.value);
  }, [field?.value]);

  // E-072 — same focus contract as the standard station: terminal mode shares
  // `runValidatedSubmit`, so it hits the identical error-stop and must hand the
  // cursor back the identical way.
  useFieldFocusAndSelect(fieldIndex, isActive, inputRef);

  const handleBlur = useCallback(() => {
    if (!field) return;

    if (localValue !== field.value) {
      setFieldValue(field.id, localValue);
      incrementSessionStat("fieldsKeyed");

      // Track errors fixed: field was fail, now might pass after rules
      if (field.validationStatus === "fail") {
        incrementSessionStat("errorsFixed");
      }
    }
  }, [field, localValue, setFieldValue, incrementSessionStat]);

  const handleFocus = useCallback(() => {
    setActiveField(fieldIndex);
  }, [fieldIndex, setActiveField]);

  if (!field) return null;

  const indicator = getStatusIndicator(field, isActive, verifyThreshold);
  const dotCount = Math.max(2, maxLabelLength - field.label.length + 2);
  const dots = ".".repeat(dotCount);

  return (
    <div
      className={`tm-field-row ${isActive ? "tm-field-active" : ""}`}
      data-testid="tm-field-row"
      data-status={field.validationStatus}
      data-active={isActive}
    >
      {isActive && <span className="tm-field-arrow" data-testid="tm-active-arrow">{"\u25B6"}</span>}
      {!isActive && <span className="tm-field-arrow">{" "}</span>}
      <span className="tm-field-label">{field.label}</span>
      <span className="tm-dot-leader" data-testid="tm-dot-leader">{dots}</span>
      <span className="tm-field-bracket">[</span>
      <input
        ref={inputRef}
        type="text"
        className="tm-field-input"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onFocus={handleFocus}
        data-field-index={fieldIndex}
        data-testid="tm-field-input"
      />
      <span className="tm-field-bracket">]</span>
      <span
        className={`tm-status-indicator tm-status-${indicator === "\u2713" ? "pass" : indicator === "\u2717" ? "fail" : indicator === "~" ? "override" : indicator === "\u2190" ? "active" : "pending"}`}
        data-testid="tm-status-indicator"
      >
        {indicator}
      </span>
    </div>
  );
}

function SessionStatsBar({ stats }: { stats: SessionStats }) {
  const [, setTick] = useState(0);

  // Update every second for live timer
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const elapsed = stats.startTime > 0 ? Date.now() - stats.startTime : 0;

  return (
    <div className="tm-stats" data-testid="tm-session-stats">
      <div className="tm-stats-divider">{"\u2500".repeat(3)} Session Stats {"\u2500".repeat(40)}</div>
      <div className="tm-stats-row">
        <span>Documents: {stats.documentsKeyed.toLocaleString()}</span>
        <span>Fields: {stats.fieldsKeyed.toLocaleString()}</span>
        <span>Errors: {stats.errorsFixed.toLocaleString()}</span>
        <span data-testid="tm-docs-per-hour">{formatDocsPerHour(stats.documentsKeyed, elapsed)} docs/hr</span>
      </div>
      <div className="tm-stats-row">
        <span>Session: {formatDuration(elapsed)}</span>
        <span>Avg: {formatAvgTimePerDoc(stats.documentsKeyed, elapsed)}/doc</span>
      </div>
    </div>
  );
}

export function TerminalModeView() {
  const fields = usePaneStore((s) => s.fields);
  // E-050 — stats line leads with the unique batch identity, not the
  // filename-derived free-text name.
  const batchLabel = usePaneStore(selectBatchLabel);
  const classification = usePaneStore((s) => s.classification);
  const currentPageIndex = usePaneStore((s) => s.currentPageIndex);
  const pages = usePaneStore((s) => s.pages);
  const activeFieldIndex = usePaneStore((s) => s.activeFieldIndex);
  const sessionStats = usePaneStore((s) => s.sessionStats);
  const focusMode = usePaneStore((s) => s.focusMode);

  const maxLabelLength = useMemo(
    () => Math.max(...fields.map((f) => f.label.length), 10),
    [fields],
  );

  const visibleFields = useMemo(() => {
    if (!focusMode) return fields.map((_, i) => i);
    return fields
      .map((f, i) => (f.needsAttention ? i : -1))
      .filter((i) => i >= 0);
  }, [fields, focusMode]);

  return (
    <div className="terminal-mode" data-testid="terminal-mode-view">
      <div className="tm-header">
        <span className="tm-title">TERMINAL MODE</span>
        <span className="tm-meta">
          {batchLabel} | {classification ?? "Unclassified"} | Page {currentPageIndex + 1}/{pages.length} | {activeFieldIndex + 1} of {fields.length} fields
        </span>
      </div>

      <div className="tm-fields-scroll">
        {visibleFields.map((fieldIndex) => (
          <TerminalField
            key={fields[fieldIndex]!.id}
            fieldIndex={fieldIndex}
            maxLabelLength={maxLabelLength}
          />
        ))}
      </div>

      <SessionStatsBar stats={sessionStats} />

      <style>{`
        .terminal-mode {
          display: flex;
          flex-direction: column;
          height: 100%;
          font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', 'Consolas', monospace;
          background: var(--color-bg-primary);
          color: var(--color-text-primary);
          overflow: hidden;
        }
        .tm-header {
          display: flex;
          align-items: center;
          gap: var(--space-4);
          padding: var(--space-2) var(--space-3);
          border-bottom: 1px solid var(--color-border);
          background: var(--color-bg-secondary);
          font-size: 0.75rem;
        }
        .tm-title {
          font-weight: 700;
          color: var(--color-primary);
          letter-spacing: 0.05em;
        }
        .tm-meta {
          color: var(--color-text-muted);
        }
        .tm-fields-scroll {
          flex: 1;
          overflow-y: auto;
          padding: var(--space-2) var(--space-3);
        }
        .tm-field-row {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 2px var(--space-2);
          font-size: 0.85rem;
          line-height: 1.8;
          white-space: nowrap;
        }
        .tm-field-row.tm-field-active {
          background: color-mix(in srgb, var(--color-primary) 10%, transparent);
          border-radius: var(--radius-sm);
        }
        .tm-field-arrow {
          display: inline-block;
          width: 1.2em;
          text-align: center;
          color: var(--color-primary);
          font-size: 0.7rem;
        }
        .tm-field-label {
          color: var(--color-text-secondary);
          flex-shrink: 0;
        }
        .tm-dot-leader {
          color: var(--color-text-muted);
          opacity: 0.4;
          flex-shrink: 0;
        }
        .tm-field-bracket {
          color: var(--color-text-muted);
          flex-shrink: 0;
        }
        .tm-field-input {
          border: none;
          outline: none;
          background: transparent;
          color: var(--color-text-primary);
          font-family: inherit;
          font-size: inherit;
          padding: 0 4px;
          min-width: 180px;
          max-width: 300px;
          border-bottom: 1px solid transparent;
        }
        .tm-field-row.tm-field-active .tm-field-input {
          border-bottom-color: var(--color-primary);
        }
        .tm-status-indicator {
          display: inline-block;
          width: 1.5em;
          text-align: center;
          flex-shrink: 0;
          font-weight: 700;
        }
        .tm-status-pass { color: var(--color-success); }
        .tm-status-fail { color: var(--color-error); }
        .tm-status-override { color: #d97706; }
        .tm-status-active { color: var(--color-primary); }
        .tm-status-pending { color: var(--color-text-muted); }

        .tm-stats {
          padding: var(--space-2) var(--space-3);
          border-top: 1px solid var(--color-border);
          background: var(--color-bg-secondary);
          font-size: 0.75rem;
          color: var(--color-text-muted);
        }
        .tm-stats-divider {
          opacity: 0.5;
          margin-bottom: var(--space-1);
          overflow: hidden;
          text-overflow: clip;
          white-space: nowrap;
        }
        .tm-stats-row {
          display: flex;
          gap: var(--space-4);
          line-height: 1.6;
        }
      `}</style>
    </div>
  );
}
